import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Terminal as TerminalIcon, X, ChevronRight, Loader2, Zap, Shield, Globe, Cpu } from 'lucide-react';

interface LatticeConsoleProps {
  isOpen: boolean;
  onClose: () => void;
  onAudit: () => void;
}

export const LatticeConsole: React.FC<LatticeConsoleProps> = ({ isOpen, onClose, onAudit }) => {
  const [history, setHistory] = useState<{ type: 'cmd' | 'res' | 'err', text: string }[]>([
    { type: 'res', text: 'ABSOLUTE ZERO LATTICE v2.0.0' },
    { type: 'res', text: 'EPOCH: 14350 BP (MIYAKE_SYNC_ACTIVE)' },
    { type: 'res', text: 'Type "help" for a list of sovereign commands.' },
  ]);
  const [input, setInput] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history]);

  const processCommand = async (cmd: string) => {
    const command = cmd.toLowerCase().trim();
    setHistory(prev => [...prev, { type: 'cmd', text: cmd }]);
    setIsProcessing(true);

    // Simulate network latency
    await new Promise(r => setTimeout(r, 600));

    switch (command) {
      case 'help':
        setHistory(prev => [...prev, { type: 'res', text: `
AVAILABLE COMMANDS:
  status   - Check global lattice synchronization
  derive   - Initiate 1×1=2 truth derivation
  audit    - Run complete system integrity check
  nodes    - List active sovereign nodes in the fleet
  sync     - Force synchronization with 14350 BP anchor
  clear    - Clear the console history
  exit     - Close the terminal interface
        ` }]);
        break;
      case 'status':
        setHistory(prev => [...prev, { type: 'res', text: 'LATTICE STATUS: OPTIMIZED\nDRIFT: 0.000000s\nNODES: 1,024,892 ONLINE\nINTEGRITY: 100%' }]);
        break;
      case 'audit':
        setHistory(prev => [...prev, { type: 'res', text: 'INITIATING SYSTEM INTEGRITY AUDIT...' }]);
        setTimeout(onAudit, 500);
        break;
      case 'derive':
        setHistory(prev => [...prev, { type: 'res', text: 'INITIATING VORTEX ENGINE...\nMATCHING MISSION TO OPPORTUNITY...\nDERIVATION COMPLETE: 1×1=2' }]);
        break;
      case 'nodes':
        setHistory(prev => [...prev, { type: 'res', text: 'SCANNING GLOBAL LATTICE...\nNODES ONLINE: 1,024,892\nACTIVE HANDSHAKES: 14,350/sec\nSIGNAL INTEGRITY: 99.99%\nSTATUS: ALL SYSTEMS NOMINAL. THE FLEET IS SYNCHRONIZED.' }]);
        break;
      case 'sync':
        setHistory(prev => [...prev, { type: 'res', text: 'SYNCHRONIZING WITH MIYAKE EVENT (14350 BP)...\n[||||||||||] 100%\nANCHOR VERIFIED.' }]);
        break;
      case 'clear':
        setHistory([]);
        break;
      case 'exit':
        onClose();
        break;
      default:
        setHistory(prev => [...prev, { type: 'err', text: `Unknown command: "${cmd}". Type "help" for assistance.` }]);
    }

    setIsProcessing(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && !isProcessing) {
      processCommand(input);
      setInput('');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/90 backdrop-blur-md"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="w-full max-w-4xl h-[600px] bg-[#050505] border border-lattice-cyan/20 rounded-xl overflow-hidden shadow-[0_0_50px_rgba(0,255,213,0.1)] flex flex-col relative z-10"
          >
            {/* Terminal Header */}
            <div className="bg-white/5 border-b border-white/10 px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <TerminalIcon className="w-4 h-4 text-lattice-cyan" />
                <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-white/60">Lattice Sovereign Console</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-[8px] font-mono text-green-400 uppercase tracking-widest">Connected</span>
                </div>
                <button onClick={onClose} className="text-white/20 hover:text-white transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Terminal Body */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 font-mono text-xs space-y-2 scrollbar-hide">
              {history.map((line, i) => (
                <div key={i} className={`whitespace-pre-wrap ${
                  line.type === 'cmd' ? 'text-white' : 
                  line.type === 'err' ? 'text-red-400' : 
                  'text-lattice-cyan/80'
                }`}>
                  {line.type === 'cmd' && <span className="text-lattice-cyan mr-2">$</span>}
                  {line.text}
                </div>
              ))}
              {isProcessing && (
                <div className="flex items-center gap-2 text-lattice-cyan/40">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  <span>PROCESSING...</span>
                </div>
              )}
            </div>

            {/* Terminal Input */}
            <form onSubmit={handleSubmit} className="p-4 bg-white/5 border-t border-white/10 flex items-center gap-3">
              <ChevronRight className="w-4 h-4 text-lattice-cyan" />
              <input
                autoFocus
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Enter command..."
                className="flex-1 bg-transparent border-none outline-none font-mono text-xs text-white placeholder:text-white/10"
              />
            </form>

            {/* Scanline Effect */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.03] bg-[linear-gradient(rgba(18,16,16,0)_50%,rgba(0,0,0,0.25)_50%),linear-gradient(90deg,rgba(255,0,0,0.06),rgba(0,255,0,0.02),rgba(0,0,255,0.06))] z-50 bg-[length:100%_2px,3px_100%]" />
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
